import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BarChart3, AlertTriangle, Server, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ProgressBar({ value, max, label, sublabel, warning = 80, critical = 95 }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const color = pct >= critical ? 'bg-red-500' : pct >= warning ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="text-muted-foreground">{sublabel || `${value}/${max}`}</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function CapacityDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getAuthHeader } = useAuthStore();

  const fetchData = useCallback(async () => {
    try {
      const [hostsRes, groupsRes] = await Promise.all([
        axios.get(`${API}/hosts`, { headers: getAuthHeader() }),
        axios.get(`${API}/hostgroups`, { headers: getAuthHeader() }),
      ]);
      setData({ hosts: hostsRes.data, groups: groupsRes.data });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Skeleton className="h-[400px] w-full" />;

  const hosts = data?.hosts?.hosts || [];
  const summary = data?.hosts?.summary || {};
  const groups = data?.groups?.groups || [];

  const totalHosts = summary.total || 0;
  const upHosts = summary.up || 0;
  const warningHosts = summary.warning || 0;
  const downHosts = summary.down || 0;
  const disabledHosts = summary.disabled || 0;

  // Group stats
  const groupStats = groups.map(g => ({
    name: g.name,
    total: g.total_hosts || 0,
    problems: g.hosts_with_problems || 0,
    ok: g.hosts_ok || 0,
  })).filter(g => g.total > 0).sort((a, b) => (b.problems / Math.max(b.total, 1)) - (a.problems / Math.max(a.total, 1)));

  // Hosts with most problems
  const criticalHosts = hosts.filter(h => h.status === 'down' || h.status === 'warning').sort((a, b) => b.problems_count - a.problems_count);

  return (
    <div className="space-y-6" data-testid="capacity-dashboard">
      <h2 className="text-lg font-semibold">Dashboard de Capacidade</h2>

      {/* Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card><CardContent className="p-4 text-center">
          <p className="text-3xl font-bold">{totalHosts}</p>
          <p className="text-xs text-muted-foreground">Total Hosts</p>
        </CardContent></Card>
        <Card className="border-green-200"><CardContent className="p-4 text-center">
          <p className="text-3xl font-bold text-green-600">{upHosts}</p>
          <p className="text-xs text-muted-foreground">UP</p>
        </CardContent></Card>
        <Card className="border-amber-200"><CardContent className="p-4 text-center">
          <p className="text-3xl font-bold text-amber-500">{warningHosts}</p>
          <p className="text-xs text-muted-foreground">Warning</p>
        </CardContent></Card>
        <Card className="border-red-200"><CardContent className="p-4 text-center">
          <p className="text-3xl font-bold text-red-600">{downHosts}</p>
          <p className="text-xs text-muted-foreground">Down</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-3xl font-bold text-gray-400">{disabledHosts}</p>
          <p className="text-xs text-muted-foreground">Desabilitados</p>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Groups Capacity */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />Capacidade por Grupo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <div className="space-y-4 pr-3">
                {groupStats.slice(0, 20).map((g, i) => (
                  <ProgressBar key={i} label={g.name} value={g.problems} max={g.total}
                    sublabel={`${g.problems} problemas / ${g.total} hosts`} warning={30} critical={60} />
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Critical Hosts */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />Hosts Críticos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <div className="space-y-2 pr-3">
                {criticalHosts.slice(0, 20).map((h, i) => (
                  <div key={h.id} className="flex items-center gap-3 p-2 rounded border text-sm">
                    <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${h.status === 'down' ? 'bg-red-500' : 'bg-amber-500'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{h.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{h.current_trigger || '-'}</p>
                    </div>
                    <Badge variant="outline" className="text-xs shrink-0">{h.category}</Badge>
                    <span className="text-xs font-mono text-muted-foreground">{h.availability}%</span>
                  </div>
                ))}
                {criticalHosts.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Nenhum host crítico</p>}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
